import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Download, Search } from "lucide-react";
import { formatINR } from "../../utils/formatINR";
import { formatDecimal, formatNumber } from "../../utils/analyticsMetrics";
import { formatHours, formatReportDate } from "../../utils/stationHistory";
import { downloadCSV, csvAmount } from "../../utils/finance";
import { TableSkeleton } from "../Analytics/AnalyticsUI";

const th = "whitespace-nowrap px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-500";
const td = "whitespace-nowrap px-4 py-2.5 tabular-nums";

const StationName = ({ id, nameOf }) => (
  <span className="flex min-w-0 items-center gap-2">
    <span className="truncate font-medium text-brand-950">{nameOf(id)}</span>
    {nameOf(id) !== id && (
      <span className="flex-shrink-0 rounded bg-neutral-50 px-1.5 py-0.5 font-mono text-[10px] text-neutral-500 ring-1 ring-inset ring-neutral-200">{id}</span>
    )}
  </span>
);

// ---------- 6. date-wise station reports ----------

const PAGE_DAYS = 10;

export const DateWiseReport = ({ groups, nameOf, isLoading }) => {
  const [open, setOpen] = useState(() => new Set());
  const [shown, setShown] = useState(PAGE_DAYS);
  const maxFinal = Math.max(...groups.map((g) => g.final), 0);

  const toggle = (date) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });
  const allOpen = groups.length > 0 && groups.slice(0, shown).every((g) => open.has(g.date));

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <h3 className="section-title">Date-wise station report</h3>
          <p className="mt-0.5 text-13 text-muted">Each day with charging activity. Expand a day to see every station.</p>
        </div>
        {groups.length > 0 && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setOpen(allOpen ? new Set() : new Set(groups.slice(0, shown).map((g) => g.date)))}
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="p-5">
          <TableSkeleton rows={6} />
        </div>
      ) : groups.length === 0 ? (
        <p className="px-5 py-12 text-center text-13 text-muted">No charging sessions found for the selected range.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-13">
              <thead className="bg-neutral-50">
                <tr>
                  <th className={`${th} text-left`}>Date</th>
                  <th className={`${th} text-right`}>Stations</th>
                  <th className={`${th} text-right`}>Sessions</th>
                  <th className={`${th} text-right`}>Energy</th>
                  <th className={`${th} text-right`}>Charging time</th>
                  <th className={`${th} text-right`}>Base cost</th>
                  <th className={`${th} text-right`}>GST</th>
                  <th className={`${th} text-right`}>Final revenue</th>
                </tr>
              </thead>
              {groups.slice(0, shown).map((g) => {
                const isOpen = open.has(g.date);
                return (
                  <tbody key={g.date} className="border-t border-neutral-100">
                    <tr onClick={() => toggle(g.date)} className={`cursor-pointer transition-colors ${isOpen ? "bg-brand-50/40" : "hover:bg-neutral-50"}`}>
                      <td className={`${td} text-left`}>
                        <span className="flex items-center gap-2">
                          {isOpen ? <ChevronDown className="h-4 w-4 text-neutral-500" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />}
                          <span className="font-semibold text-brand-950">{formatReportDate(g.date)}</span>
                        </span>
                      </td>
                      <td className={`${td} text-right text-neutral-600`}>{formatNumber(g.stations.length)}</td>
                      <td className={`${td} text-right text-neutral-700`}>{formatNumber(g.sessions)}</td>
                      <td className={`${td} text-right text-neutral-700`}>{formatDecimal(g.energy)} kWh</td>
                      <td className={`${td} text-right text-neutral-700`}>{formatHours(g.duration)}</td>
                      <td className={`${td} text-right text-neutral-700`}>{formatINR(g.base)}</td>
                      <td className={`${td} text-right text-neutral-700`}>{formatINR(g.gst)}</td>
                      <td className={`${td} text-right`}>
                        <span className="inline-flex items-center gap-3">
                          <span className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-neutral-100 lg:block">
                            <span className="block h-full rounded-full bg-brand-600" style={{ width: `${maxFinal ? (g.final / maxFinal) * 100 : 0}%` }} />
                          </span>
                          <span className="font-semibold text-fg">{formatINR(g.final)}</span>
                        </span>
                      </td>
                    </tr>
                    {isOpen &&
                      g.stations.map((r) => (
                        <tr key={r.id} className="bg-neutral-50/60 text-neutral-600">
                          <td className={`${td} pl-11 text-left`}>
                            <StationName id={r.stationId} nameOf={nameOf} />
                          </td>
                          <td className={td} />
                          <td className={`${td} text-right`}>{formatNumber(r.sessions)}</td>
                          <td className={`${td} text-right`}>{formatDecimal(r.energy)} kWh</td>
                          <td className={`${td} text-right`}>{formatHours(r.duration)}</td>
                          <td className={`${td} text-right`}>{formatINR(r.base)}</td>
                          <td className={`${td} text-right`}>{formatINR(r.gst)}</td>
                          <td className={`${td} text-right font-medium text-neutral-800`}>{formatINR(r.final)}</td>
                        </tr>
                      ))}
                  </tbody>
                );
              })}
            </table>
          </div>
          {groups.length > PAGE_DAYS && (
            <div className="flex items-center justify-center gap-3 border-t border-line px-5 py-3 text-13 text-muted">
              Showing {formatNumber(Math.min(shown, groups.length))} of {formatNumber(groups.length)} days
              {shown < groups.length ? (
                <button type="button" className="btn-secondary" onClick={() => setShown((n) => n + PAGE_DAYS * 2)}>
                  Show more days
                </button>
              ) : (
                <button type="button" className="btn-ghost" onClick={() => setShown(PAGE_DAYS)}>
                  Show fewer
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- 7. detailed historical records ----------

const COLUMNS = [
  { key: "date", label: "Date", align: "left" },
  { key: "station", label: "Station", align: "left" },
  { key: "sessions", label: "Sessions" },
  { key: "energy", label: "Energy (kWh)" },
  { key: "duration", label: "Charging time" },
  { key: "base", label: "Base cost" },
  { key: "gst", label: "GST" },
  { key: "final", label: "Final revenue" },
];
const PAGE_ROWS = 25;

export const RecordsTable = ({ records, totals, nameOf, isLoading, range }) => {
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE_ROWS);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = records.filter((r) => !q || r.stationId.toLowerCase().includes(q) || nameOf(r.stationId).toLowerCase().includes(q));
    const val = (r) => (sort.key === "station" ? nameOf(r.stationId).toLowerCase() : r[sort.key]);
    return [...list].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const c = x < y ? -1 : x > y ? 1 : 0;
      return sort.dir === "asc" ? c : -c;
    });
  }, [records, query, sort, nameOf]);

  const setSortKey = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "station" || key === "date" ? "asc" : "desc" }));

  const exportCSV = () =>
    downloadCSV(`station-history_${range.fromDate}_${range.toDate}.csv`, [
      ["Date", "Station ID", "Station", "Sessions", "Energy (kWh)", "Duration (s)", "Base cost", "GST", "Final revenue"],
      ...rows.map((r) => [r.date, r.stationId, nameOf(r.stationId), r.sessions, r.energy, r.duration, csvAmount(r.base), csvAmount(r.gst), csvAmount(r.final)]),
    ]);

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="section-title">Detailed records</h3>
          <p className="mt-0.5 text-13 text-muted">One row per station per day, as returned by the server. Click a column to sort.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1 sm:w-60 sm:flex-none">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search station" className="input w-full pl-9" />
          </div>
          <button type="button" className="btn-secondary" onClick={exportCSV} disabled={!rows.length}>
            <Download className="h-3.5 w-3.5" />
            CSV
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-5">
          <TableSkeleton rows={8} />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-13">
              <thead className="bg-neutral-50">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} className={`${th} ${c.align === "left" ? "text-left" : "text-right"}`}>
                      <button
                        type="button"
                        onClick={() => setSortKey(c.key)}
                        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-fg ${sort.key === c.key ? "text-fg" : ""}`}
                      >
                        {c.label}
                        {sort.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-muted">
                      {records.length ? "No records match this search." : "No charging sessions found for the selected range."}
                    </td>
                  </tr>
                ) : (
                  rows.slice(0, shown).map((r) => (
                    <tr key={r.id} className="text-neutral-700 hover:bg-neutral-50">
                      <td className={`${td} text-left text-neutral-600`}>{r.date === "unknown" ? "Unknown" : r.date}</td>
                      <td className={`${td} max-w-[260px] text-left`}>
                        <StationName id={r.stationId} nameOf={nameOf} />
                      </td>
                      <td className={`${td} text-right`}>{formatNumber(r.sessions)}</td>
                      <td className={`${td} text-right`}>{formatDecimal(r.energy)}</td>
                      <td className={`${td} text-right`}>{formatHours(r.duration)}</td>
                      <td className={`${td} text-right`}>{formatINR(r.base)}</td>
                      <td className={`${td} text-right`}>{formatINR(r.gst)}</td>
                      <td className={`${td} text-right font-semibold text-fg`}>{formatINR(r.final)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length > 0 && !query && (
                <tfoot className="border-t border-line bg-neutral-50 font-semibold text-fg">
                  <tr>
                    <td className={`${td} text-left`} colSpan={2}>
                      Total · {formatNumber(records.length)} records
                    </td>
                    <td className={`${td} text-right`}>{formatNumber(totals.sessions)}</td>
                    <td className={`${td} text-right`}>{formatDecimal(totals.energy)}</td>
                    <td className={`${td} text-right`}>{formatHours(totals.duration)}</td>
                    <td className={`${td} text-right`}>{formatINR(totals.base)}</td>
                    <td className={`${td} text-right`}>{formatINR(totals.gst)}</td>
                    <td className={`${td} text-right`}>{formatINR(totals.final)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length > PAGE_ROWS && (
            <div className="flex items-center justify-center gap-3 border-t border-line px-5 py-3 text-13 text-muted">
              Showing {formatNumber(Math.min(shown, rows.length))} of {formatNumber(rows.length)} records
              {shown < rows.length ? (
                <button type="button" className="btn-secondary" onClick={() => setShown((n) => n + PAGE_ROWS * 4)}>
                  Show more
                </button>
              ) : (
                <button type="button" className="btn-ghost" onClick={() => setShown(PAGE_ROWS)}>
                  Show fewer
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
